import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "ui/card";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Label } from "ui/label";
import { Badge } from "ui/badge";
import { Checkbox } from "ui/checkbox";
import { Switch } from "ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import {
  Search,
  Plus,
  Edit2,
  X,
  Building2,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Check,
  Home,
  Trash2,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { useScreenPermission } from "hooks/useScreenPermission";
import {
  getProperties,
  getPropertyTypes,
  getPropertyGrades,
  deleteProperty,
  updatePropertyStatus, 
  getPropertyStats,
} from "services/propertyDetailsService";
import Pagination from "ui/pagination";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";

interface PropertyDetails {
  id: string;
  propertyName: string;
  grade: string;
  type: string;
  personInAvailability: string[];
  isLiftAvailable: boolean;
  isHousekeepingAvailable: boolean;
  hasGroundFloor: boolean;
  hasBasement: boolean;
  isRented: boolean;
  noOfFloors: number;
  noOfRooms: number;
  capacity: {
    base: number;
    buffer: number;
    extra: number;
    total: number;
    adults: number;
    children: number;
  };
  groupCheckInCheckOut: string;
  propertyAvailable: boolean;
  createdOn: string;
}

const PERSON_AVAILABILITY_OPTIONS = [
  "Mumineen",
  "Rohe Zamy",
  "Qarare Aani",
  "Qarare Aani Jamel",
  "Staff",
];

interface PropertyDetailsMasterPageProps {
  onNavigate?: (page: string, property?: PropertyDetails) => void;
  initialProperties?: PropertyDetails[];
  onUpdateProperties?: (properties: PropertyDetails[]) => void;
}

export const PropertyDetailsMasterPage = ({
  onNavigate,
  initialProperties = [],
  onUpdateProperties,
}: PropertyDetailsMasterPageProps) => {
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [editingItem, setEditingItem] = useState<PropertyDetails | null>(null);
  const [loading, setLoading] = useState(false);

  const [propertyTypeOptions, setPropertyTypeOptions] = useState<
    { id: number; name: string }[]
  >([]);
  const [propertyGradeOptions, setPropertyGradeOptions] = useState<string[]>(
    [],
  );

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<PropertyDetails | null>(
    null,
  );

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const { canAdd, canEdit, canDelete, screenName, icon: ScreenIcon } = useScreenPermission();

   useEffect(() => {
    setPageHeader({
      title: screenName || "Property Details",
      subtitle: "Manage all property information and configurations",
      icon: <ScreenIcon className="h-4 w-4" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  // Filter states
  const [filters, setFilters] = useState({
    propertyName: "",
    type: "all",
    grade: "all",
    status: "all",
  });

  const [stats, setStats] = useState({
    totalProperties: 0,
    available: 0,
    totalRooms: 0,
  });

  const defaultFormData = {
    propertyName: "",
    grade: "",
    type: "",
    personInAvailability: [] as string[],
    isLiftAvailable: false,
    isHousekeepingAvailable: false,
    hasGroundFloor: false,
    hasBasement: false,
    isRented: false,
    noOfFloors: 0,
    noOfRooms: 0,
    capacityBase: 0,
    capacityBuffer: 0,
    capacityExtra: 0,
    groupCheckInCheckOut: "",
  };

  const [properties, setProperties] = useState<PropertyDetails[]>([]);

  // Helper function to update properties and sync with parent
  const updateProperties = (newProperties: PropertyDetails[]) => {
    setProperties(newProperties);
    if (onUpdateProperties) {
      onUpdateProperties(newProperties);
    }
  };

  const getPropertyTypeName = (id: number) => {
    return propertyTypeOptions.find((t) => t.id === id)?.name || "N/A";
  };

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        const res = await getProperties(pageNo, pageSize);
        const formatted: PropertyDetails[] = res.items.map((item) => ({
          id: item.publicId,
          propertyName: item.propertyName,
          grade: item.propertyGrade || "-",
          type:
            item.propertyTypeName?.trim() ||
            getPropertyTypeName(item.propertyTypeId) ||
            "N/A",
          personInAvailability: [],
          isLiftAvailable: false,
          isHousekeepingAvailable: false,
          hasGroundFloor: false,
          hasBasement: false,
          isRented: false,
          noOfFloors: item.totalFloors || 0,
          noOfRooms: item.totalRooms || 0,
          capacity: {
            base: item.capacity || 0,
            buffer: 0,
            extra: 0,
            total: item.capacity || 0,
            adults: item.maxAdults || 0,
            children: item.maxChildren || 0,
          },
          groupCheckInCheckOut: item.checkInTime?.slice(0, 8) || "",
          propertyAvailable: item.isActive,
          createdOn: "",
        }));

        setProperties(formatted);
        setTotalCount(res.totalCount);
      } catch (error) {
        toast.error("Failed to load properties");
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize]);

  useEffect(() => {
    const fetchPropertyTypes = async () => {
      try {
        setLoading(true);
        const data = await getPropertyTypes();

        const types = data
          .filter((t) => t.isActive)
          .map((t) => ({
            id: t.id,
            name: t.name.trim(),
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setPropertyTypeOptions(types);

        if (types.length > 0) {
          setFormData((prev) => ({
            ...prev,
            type: types[0].name,
          }));
        }
      } catch (error) {
        console.error("Failed to fetch property types", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyTypes();
  }, []);

  useEffect(() => {
    const fetchPropertyGrades = async () => {
      try {
        setLoading(true);
        const data = await getPropertyGrades();

        const grades = data.filter((g) => g.isActive).map((g) => g.name.trim());

        setPropertyGradeOptions(grades.sort());

        if (grades.length > 0) {
          setFormData((prev) => ({
            ...prev,
            grade: grades[0],
          }));
        }
      } catch (error) {
        console.error("Failed to fetch property grades", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyGrades();
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getPropertyStats();
        setStats(data);
      } catch (error) {
        console.error("Failed to fetch stats", error);
      }
    };

    fetchStats();
  }, []);

  // Apply filters
  const filteredData = properties.filter((item) => {
    const matchesSearch =
      item.propertyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.grade.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.type.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesName =
      !filters.propertyName ||
      item.propertyName
        .toLowerCase()
        .includes(filters.propertyName.toLowerCase());
    const matchesType =
      filters.type === "all" ||
      item.type?.toLowerCase().trim() === filters.type.toLowerCase().trim();
    const matchesGrade =
      filters.grade === "all" ||
      item.grade?.toLowerCase().trim() === filters.grade.toLowerCase().trim();
    const matchesStatus =
      filters.status === "all" ||
      (filters.status === "active" && item.propertyAvailable) ||
      (filters.status === "inactive" && !item.propertyAvailable);

    return (
      matchesSearch &&
      matchesName &&
      matchesType &&
      matchesGrade &&
      matchesStatus
    );
  });

  useEffect(() => {
    setPageNo(1);
  }, [searchTerm, filters]);

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn("");
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Apply sorting
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortColumn) return 0;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let aValue: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let bValue: any;

    if (sortColumn === "propertyName") {
      aValue = a.propertyName;
      bValue = b.propertyName;
    } else if (sortColumn === "grade") {
      aValue = a.grade;
      bValue = b.grade;
    } else if (sortColumn === "type") {
      aValue = a.type;
      bValue = b.type;
    } else if (sortColumn === "noOfRooms") {
      aValue = a.noOfRooms;
      bValue = b.noOfRooms;
    }

    return sortDirection === "asc"
      ? String(aValue).localeCompare(String(bValue), undefined, {
          numeric: true,
        })
      : String(bValue).localeCompare(String(aValue), undefined, {
          numeric: true,
        });
  });

  useEffect(() => {
    setPageNo(1);
  }, [searchTerm, filters]);

  const [formData, setFormData] = useState(defaultFormData);
  const [editFormData, setEditFormData] = useState(defaultFormData);

  const handleAdd = () => {
    if (!formData.propertyName.trim()) {
      toast.error("Please enter property name");
      return;
    }

    const totalCapacity =
      formData.capacityBase + formData.capacityBuffer + formData.capacityExtra;

    const newItem: PropertyDetails = {
      id: Date.now().toString(),
      propertyName: formData.propertyName,
      grade: formData.grade,
      type: formData.type,
      personInAvailability: formData.personInAvailability,
      isLiftAvailable: formData.isLiftAvailable,
      isHousekeepingAvailable: formData.isHousekeepingAvailable,
      hasGroundFloor: formData.hasGroundFloor,
      hasBasement: formData.hasBasement,
      isRented: formData.isRented,
      noOfFloors: formData.noOfFloors,
      noOfRooms: formData.noOfRooms,
      capacity: {
        base: formData.capacityBase,
        buffer: formData.capacityBuffer,
        extra: formData.capacityExtra,
        total: totalCapacity,
        adults: 0,
        children: 0,
      },
      groupCheckInCheckOut: formData.groupCheckInCheckOut,
      propertyAvailable: true,
      createdOn: new Date().toISOString().split("T")[0],
    };

    updateProperties([...properties, newItem]);
    setIsAddDialogOpen(false);
    resetForm();
    toast.success("Property added successfully");
  };

  const handleEdit = (item: PropertyDetails) => {
    navigate(`/property-details/edit/${item.id}`, {});
  };

  const handleUpdate = () => {
    if (!editingItem) return;

    if (!editFormData.propertyName.trim()) {
      toast.error("Please enter property name");
      return;
    }

    const totalCapacity =
      editFormData.capacityBase +
      editFormData.capacityBuffer +
      editFormData.capacityExtra;

    updateProperties(
      properties.map((item) =>
        item.id === editingItem.id
          ? {
              ...item,
              propertyName: editFormData.propertyName,
              grade: editFormData.grade,
              type: editFormData.type,
              personInAvailability: editFormData.personInAvailability,
              isLiftAvailable: editFormData.isLiftAvailable,
              isHousekeepingAvailable: editFormData.isHousekeepingAvailable,
              hasGroundFloor: editFormData.hasGroundFloor,
              hasBasement: editFormData.hasBasement,
              isRented: editFormData.isRented,
              noOfFloors: editFormData.noOfFloors,
              noOfRooms: editFormData.noOfRooms,
              capacity: {
                base: editFormData.capacityBase,
                buffer: editFormData.capacityBuffer,
                extra: editFormData.capacityExtra,
                total: totalCapacity,
                adults: 0,
                children: 0,
              },
              groupCheckInCheckOut: editFormData.groupCheckInCheckOut,
            }
          : item,
      ),
    );

    setIsEditDialogOpen(false);
    setEditingItem(null);
    toast.success("Property updated successfully");
  };

  const confirmDelete = async () => {
    if (!deletingItem) return;

    try {
      await deleteProperty(deletingItem.id);

      const updatedProperties = properties.filter(
        (item) => item.id !== deletingItem.id,
      );

      updateProperties(updatedProperties);
      const updatedStats = await getPropertyStats();
      setStats(updatedStats);
      toast.success("Property deleted successfully");
    } catch (error) {
      console.error("Delete failed", error);
      toast.error("Failed to delete property");
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingItem(null);
    }
  };

  const toggleStatus = async (id: string) => {
    const property = properties.find((item) => item.id === id);
    if (!property) return;
    const newStatus = !property.propertyAvailable;
    try {
      await updatePropertyStatus(id, newStatus);
      updateProperties(
        properties.map((item) =>
          item.id === id ? { ...item, propertyAvailable: newStatus } : item,
        ),
      );
      toast.success(
        `Property ${newStatus ? "activated" : "deactivated"} successfully`,
      );
    } catch (error) {
      toast.error("Failed to update property status");
    }
  };

  const togglePersonAvailability = (person: string, isEdit = false) => {
    if (isEdit) {
      setEditFormData((prev) => ({
        ...prev,
        personInAvailability: prev.personInAvailability.includes(person)
          ? prev.personInAvailability.filter((p) => p !== person)
          : [...prev.personInAvailability, person],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        personInAvailability: prev.personInAvailability.includes(person)
          ? prev.personInAvailability.filter((p) => p !== person)
          : [...prev.personInAvailability, person],
      }));
    }
  };

  const resetForm = () => {
    setFormData(defaultFormData);
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-4 sm:space-y-6 pb-8 px-4 sm:px-0">

        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-4">
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

              <Input
                placeholder="Search properties, types, or grades..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />

              {searchTerm && (
                <button
                  type="button"
                  onClick={async () => {
                    setSearchTerm("");
                    setPageNo(1);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowStatistics(!showStatistics)}
              className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10 shrink-0"
            >
              {showStatistics ? (
                <ArrowUp className="w-5 h-5" />
              ) : (
                <BarChart3 className="w-5 h-5" />
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`shrink-0 ${
                showFilters
                  ? "text-white"
                  : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
              }`}
              title="Advanced Filters"
            >
              <Filter className="w-4 h-4 sm:mr-0" />
            </Button>
            {canAdd && (
              <Button
                onClick={() => navigate("/property-details/add")}
                style={{ background: "#5B9BD5" }}
                className="text-white whitespace-nowrap"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Property
              </Button>
            )}
          </div>
        </div>
        {/* Statistics Cards */}
        {showStatistics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" style={{ marginTop: "0px" }}>
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">Total Properties</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {stats.totalProperties}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Available</p>
                    <p className="text-2xl font-bold text-green-900">
                      {stats.available}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600">Total Rooms</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {stats.totalRooms}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Home className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Advanced Filters Section */}
        {showFilters && (
          <Card className="border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50" style={{ gap: "0px", marginTop: "10px" }}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle
                    style={{ color: "#5B9BD5" }}
                    className="flex items-center gap-2 text-lg"
                  >
                    <Filter className="w-5 h-5" />
                    Advanced Filters
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Filter properties by multiple criteria
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Property Name Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-property-name" className="text-sm">
                    Property Name
                  </Label>
                  <Input
                    id="filter-property-name"
                    placeholder="Filter by name..."
                    value={filters.propertyName}
                    onChange={(e) =>
                      setFilters({ ...filters, propertyName: e.target.value })
                    }
                    className="h-9 text-sm"
                  />
                </div>

                {/* Type Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-type" className="text-sm">
                    Type
                  </Label>
                  <Select
                    value={filters.type}
                    onValueChange={(value) =>
                      setFilters({ ...filters, type: value })
                    }
                  >
                    <SelectTrigger id="filter-type" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {propertyTypeOptions.map((type) => (
                        <SelectItem key={type.name} value={String(type.name)}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Grade Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-grade" className="text-sm">
                    Grade
                  </Label>
                  <Select
                    value={filters.grade}
                    onValueChange={(value) =>
                      setFilters({ ...filters, grade: value })
                    }
                  >
                    <SelectTrigger id="filter-grade" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Grades</SelectItem>
                      {propertyGradeOptions.map((grade) => (
                        <SelectItem key={grade} value={grade}>
                          {grade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-status" className="text-sm">
                    Status
                  </Label>
                  <Select
                    value={filters.status}
                    onValueChange={(value) =>
                      setFilters({ ...filters, status: value })
                    }
                  >
                    <SelectTrigger id="filter-status" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Available Only</SelectItem>
                      <SelectItem value="inactive">Unavailable Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Active Filters Summary & Clear Button */}
              {(filters.propertyName ||
                filters.type !== "all" ||
                filters.grade !== "all" ||
                filters.status !== "all") && (
                <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#5B9BD5] mb-2">
                      Active Filters:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {filters.propertyName && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Name: {filters.propertyName}
                        </Badge>
                      )}
                      {filters.type !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Type: {filters.type}
                        </Badge>
                      )}
                      {filters.grade !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Grade: {filters.grade}
                        </Badge>
                      )}
                      {filters.status !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Status: {filters.status}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFilters({
                        propertyName: "",
                        type: "all",
                        grade: "all",
                        status: "all",
                      });
                    }}
                    className="text-red-600 border-red-300 hover:bg-red-50 flex-shrink-0"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear All
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px", marginTop: "10px" }}>
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("propertyName")}
                    >
                      <div className="flex items-center gap-1">
                        Property Name
                        {sortColumn === "propertyName" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("type")}
                    >
                      <div className="flex items-center gap-1">
                        Type
                        {sortColumn === "type" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("noOfRooms")}
                    >
                      <div className="flex items-center gap-1">
                        Rooms
                        {sortColumn === "noOfRooms" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Capacity
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs text-center"
                    >
                      Status
                    </TableHead>
                    {(canEdit || canDelete) && (
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs text-right"
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-8 px-4 text-gray-500 text-sm"
                      >
                        No properties found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedData.map((item, index) => (
                      <TableRow key={item.id} className="hover:bg-sky-50/30">
                        <TableCell className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <div
                              className="
                                w-8 h-8
                                rounded-full
                                flex items-center justify-center
                                hover:bg-sky-50/30
                                shrink-0
                              "
                            >
                              <Building2 className="w-4 h-4 text-[#1E3A5F]" />
                            </div>
                            <div className="text-gray-900 text-sm font-medium">
                              {item.propertyName}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-2">
                          <Badge
                            className={"text-xs hover:bg-sky-50/30"}
                            variant="outline"
                          >
                            {item.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-2">
                          <span className="text-sm font-medium text-gray-900">
                            {item.noOfRooms}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-2">
                          <div className="text-xs space-y-0.5">
                            <div className="text-gray-900 font-semibold">
                              Max:{" "}
                              <span className="font-bold">
                                {item.capacity.total}
                              </span>
                            </div>
                            <div className="text-gray-600">
                              <span className="font-medium">
                                {item.capacity.adults}A + {item.capacity.children}
                                C
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-2">
                          <div className="flex items-center justify-center">
                            <Switch
                              checked={item.propertyAvailable}
                              onCheckedChange={() => toggleStatus(item.id)}
                              className="data-[state=checked]:bg-[#5B9BD5]"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-2 text-right">
                          <div className="flex items-center justify-end">
                            {canEdit && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEdit(item)}
                                className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                title="Edit"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                onClick={() => {
                                  setDeletingItem(item);
                                  setIsDeleteDialogOpen(true);
                                }}
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-3 p-4">
              {sortedData.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No properties found</p>
                </div>
              ) : (
                sortedData.map((item, index) => (
                  <Card
                    key={item.id}
                    className={
                      "border-[#5B9BD5]/20 hover:border-[#5B9BD5]/40 transition-colors hover:bg-sky-50/30"
                    }
                  >
                    <CardContent style={{ padding: "10px" }}>
                      <div className="flex items-start mb-3">
                        <div className="flex items-center gap-2">
                          <div
                            className={
                              "w-10 h-10 rounded-full flex items-center justify-center hover:bg-sky-50/30 "
                            }
                          >
                            <Building2 className={"w-5 h-5 hover:bg-sky-50/30"} />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">
                              {item.propertyName}
                            </p>
                            <div className="flex gap-1 mt-1">
                              <Badge className="text-xs" variant="outline">
                                {item.type}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                        <div>
                          <span className="text-gray-500">Rooms:</span>
                          <span className="ml-1 font-medium">
                            {item.noOfRooms}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Floors:</span>
                          <span className="ml-1 font-medium">
                            {item.noOfFloors}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-500">Capacity:</span>
                          <span className="ml-1 font-medium">
                            {item.capacity.total}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Status:</span>
                          <Switch
                            checked={item.propertyAvailable}
                            onCheckedChange={() => toggleStatus(item.id)}
                            className="data-[state=checked]:bg-[#5B9BD5]"
                          />
                          <span
                            className={`text-xs font-medium ${item.propertyAvailable ? "text-green-700" : "text-gray-600"}`}
                          >
                            {item.propertyAvailable ? "Active" : "Inactive"}
                          </span>
                        </div>
                        <div>
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(item)}
                              className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                              title="Edit"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              onClick={() => {
                                setDeletingItem(item);
                                setIsDeleteDialogOpen(true);
                              }}
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>

          {/* {totalCount > pageSize && ( */}
            <Pagination
              totalItems={totalCount}
              itemsPerPage={pageSize}
              currentPage={pageNo}
              onPageChange={(page) => setPageNo(page)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPageNo(1);
              }}
              label="properties"
            />
          {/* )}  */}
        </Card>

        {/* Add Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>Add Property</DialogTitle>
              <DialogDescription>
                Create a new property entry with all details
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="propertyName" className="text-sm">
                    Property Name *
                  </Label>
                  <Input
                    id="propertyName"
                    value={formData.propertyName}
                    onChange={(e) =>
                      setFormData({ ...formData, propertyName: e.target.value })
                    }
                    placeholder="e.g., Qasr Al Dur 1447"
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type" className="text-sm">
                    Type *
                  </Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) =>
                      setFormData({ ...formData, type: value })
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyTypeOptions.map((type) => (
                        <SelectItem key={type.id} value={type.name}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="grade" className="text-sm">
                    Grade *
                  </Label>
                  <Select
                    value={formData.grade}
                    onValueChange={(value) =>
                      setFormData({ ...formData, grade: value })
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyGradeOptions.map((grade) => (
                        <SelectItem key={grade} value={grade}>
                          {grade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="noOfFloors" className="text-sm">
                    No. of Floors
                  </Label>
                  <Input
                    id="noOfFloors"
                    type="number"
                    value={formData.noOfFloors}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        noOfFloors: parseInt(e.target.value) || 0,
                      })
                    }
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="noOfRooms" className="text-sm">
                    No. of Rooms
                  </Label>
                  <Input
                    id="noOfRooms"
                    type="number"
                    value={formData.noOfRooms}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        noOfRooms: parseInt(e.target.value) || 0,
                      })
                    }
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="groupCheckInCheckOut" className="text-sm">
                    Check-in Time
                  </Label>
                  <Input
                    id="groupCheckInCheckOut"
                    value={formData.groupCheckInCheckOut}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        groupCheckInCheckOut: e.target.value,
                      })
                    }
                    placeholder="e.g., 2:00 PM"
                    className="h-9 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="capacityBase" className="text-sm">
                    Base Capacity
                  </Label>
                  <Input
                    id="capacityBase"
                    type="number"
                    value={formData.capacityBase}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        capacityBase: parseInt(e.target.value) || 0,
                      })
                    }
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="capacityBuffer" className="text-sm">
                    Buffer Capacity
                  </Label>
                  <Input
                    id="capacityBuffer"
                    type="number"
                    value={formData.capacityBuffer}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        capacityBuffer: parseInt(e.target.value) || 0,
                      })
                    }
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="capacityExtra" className="text-sm">
                    Extra Capacity
                  </Label>
                  <Input
                    id="capacityExtra"
                    type="number"
                    value={formData.capacityExtra}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        capacityExtra: parseInt(e.target.value) || 0,
                      })
                    }
                    className="h-9 text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Person Availability</Label>
                <div className="grid grid-cols-2 gap-2">
                  {PERSON_AVAILABILITY_OPTIONS.map((person) => (
                    <div key={person} className="flex items-center space-x-2">
                      <Checkbox
                        id={`person-${person}`}
                        checked={formData.personInAvailability.includes(person)}
                        onCheckedChange={() => togglePersonAvailability(person)}
                      />
                      <label
                        htmlFor={`person-${person}`}
                        className="text-sm cursor-pointer"
                      >
                        {person}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Amenities & Features</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isLiftAvailable"
                      checked={formData.isLiftAvailable}
                      onCheckedChange={(checked) =>
                        setFormData({
                          ...formData,
                          isLiftAvailable: checked as boolean,
                        })
                      }
                    />
                    <label
                      htmlFor="isLiftAvailable"
                      className="text-sm cursor-pointer"
                    >
                      Lift Available
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isHousekeepingAvailable"
                      checked={formData.isHousekeepingAvailable}
                      onCheckedChange={(checked) =>
                        setFormData({
                          ...formData,
                          isHousekeepingAvailable: checked as boolean,
                        })
                      }
                    />
                    <label
                      htmlFor="isHousekeepingAvailable"
                      className="text-sm cursor-pointer"
                    >
                      Housekeeping Available
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="hasGroundFloor"
                      checked={formData.hasGroundFloor}
                      onCheckedChange={(checked) =>
                        setFormData({
                          ...formData,
                          hasGroundFloor: checked as boolean,
                        })
                      }
                    />
                    <label
                      htmlFor="hasGroundFloor"
                      className="text-sm cursor-pointer"
                    >
                      Has Ground Floor
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="hasBasement"
                      checked={formData.hasBasement}
                      onCheckedChange={(checked) =>
                        setFormData({
                          ...formData,
                          hasBasement: checked as boolean,
                        })
                      }
                    />
                    <label
                      htmlFor="hasBasement"
                      className="text-sm cursor-pointer"
                    >
                      Has Basement
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isRented"
                      checked={formData.isRented}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, isRented: checked as boolean })
                      }
                    />
                    <label htmlFor="isRented" className="text-sm cursor-pointer">
                      Is Rented
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAdd}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Property
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog - Similar structure to Add Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                Edit Property
              </DialogTitle>
              <DialogDescription>Update property information</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-propertyName" className="text-sm">
                    Property Name *
                  </Label>
                  <Input
                    id="edit-propertyName"
                    value={editFormData.propertyName}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        propertyName: e.target.value,
                      })
                    }
                    placeholder="e.g., Qasr Al Dur 1447"
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-type" className="text-sm">
                    Type *
                  </Label>
                  <Select
                    value={editFormData.type}
                    onValueChange={(value) =>
                      setEditFormData({ ...editFormData, type: value })
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    {propertyTypeOptions.map((type) => (
                      <SelectItem key={type.id} value={type.name}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-grade" className="text-sm">
                    Grade *
                  </Label>
                  <Select
                    value={editFormData.grade}
                    onValueChange={(value) =>
                      setEditFormData({ ...editFormData, grade: value })
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    {propertyGradeOptions.map((grade) => (
                      <SelectItem key={grade} value={grade}>
                        {grade}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-noOfFloors" className="text-sm">
                    No. of Floors
                  </Label>
                  <Input
                    id="edit-noOfFloors"
                    type="number"
                    value={editFormData.noOfFloors}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        noOfFloors: parseInt(e.target.value) || 0,
                      })
                    }
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-noOfRooms" className="text-sm">
                    No. of Rooms
                  </Label>
                  <Input
                    id="edit-noOfRooms"
                    type="number"
                    value={editFormData.noOfRooms}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        noOfRooms: parseInt(e.target.value) || 0,
                      })
                    }
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-groupCheckInCheckOut" className="text-sm">
                    Check-in Time
                  </Label>
                  <Input
                    id="edit-groupCheckInCheckOut"
                    value={editFormData.groupCheckInCheckOut}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        groupCheckInCheckOut: e.target.value,
                      })
                    }
                    placeholder="e.g., 2:00 PM"
                    className="h-9 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-capacityBase" className="text-sm">
                    Base Capacity
                  </Label>
                  <Input
                    id="edit-capacityBase"
                    type="number"
                    value={editFormData.capacityBase}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        capacityBase: parseInt(e.target.value) || 0,
                      })
                    }
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-capacityBuffer" className="text-sm">
                    Buffer Capacity
                  </Label>
                  <Input
                    id="edit-capacityBuffer"
                    type="number"
                    value={editFormData.capacityBuffer}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        capacityBuffer: parseInt(e.target.value) || 0,
                      })
                    }
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-capacityExtra" className="text-sm">
                    Extra Capacity
                  </Label>
                  <Input
                    id="edit-capacityExtra"
                    type="number"
                    value={editFormData.capacityExtra}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        capacityExtra: parseInt(e.target.value) || 0,
                      })
                    }
                    className="h-9 text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Person Availability</Label>
                <div className="grid grid-cols-2 gap-2">
                  {PERSON_AVAILABILITY_OPTIONS.map((person) => (
                    <div key={person} className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-person-${person}`}
                        checked={editFormData.personInAvailability.includes(
                          person,
                        )}
                        onCheckedChange={() =>
                          togglePersonAvailability(person, true)
                        }
                      />
                      <label
                        htmlFor={`edit-person-${person}`}
                        className="text-sm cursor-pointer"
                      >
                        {person}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Amenities & Features</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-isLiftAvailable"
                      checked={editFormData.isLiftAvailable}
                      onCheckedChange={(checked) =>
                        setEditFormData({
                          ...editFormData,
                          isLiftAvailable: checked as boolean,
                        })
                      }
                    />
                    <label
                      htmlFor="edit-isLiftAvailable"
                      className="text-sm cursor-pointer"
                    >
                      Lift Available
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-isHousekeepingAvailable"
                      checked={editFormData.isHousekeepingAvailable}
                      onCheckedChange={(checked) =>
                        setEditFormData({
                          ...editFormData,
                          isHousekeepingAvailable: checked as boolean,
                        })
                      }
                    />
                    <label
                      htmlFor="edit-isHousekeepingAvailable"
                      className="text-sm cursor-pointer"
                    >
                      Housekeeping Available
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-hasGroundFloor"
                      checked={editFormData.hasGroundFloor}
                      onCheckedChange={(checked) =>
                        setEditFormData({
                          ...editFormData,
                          hasGroundFloor: checked as boolean,
                        })
                      }
                    />
                    <label
                      htmlFor="edit-hasGroundFloor"
                      className="text-sm cursor-pointer"
                    >
                      Has Ground Floor
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-hasBasement"
                      checked={editFormData.hasBasement}
                      onCheckedChange={(checked) =>
                        setEditFormData({
                          ...editFormData,
                          hasBasement: checked as boolean,
                        })
                      }
                    />
                    <label
                      htmlFor="edit-hasBasement"
                      className="text-sm cursor-pointer"
                    >
                      Has Basement
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-isRented"
                      checked={editFormData.isRented}
                      onCheckedChange={(checked) =>
                        setEditFormData({
                          ...editFormData,
                          isRented: checked as boolean,
                        })
                      }
                    />
                    <label
                      htmlFor="edit-isRented"
                      className="text-sm cursor-pointer"
                    >
                      Is Rented
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdate}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Update Property
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-red-600">
                Delete Property Details
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-semibold">
                  "{deletingItem?.propertyName}"
                </span>
                ? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingItem(null);
                }}
              >
                No, Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
